/** Maps marketing niche slugs → onboarding trade_type (DB enum). */
export const NICHE_TO_TRADE_TYPE = {
  hvac: 'hvac',
  plumbing: 'plumbing',
  electrical: 'electrical',
  'pest-control': 'other',
  'lawn-care': 'other',
  'pool-spa': 'other',
  'garage-door': 'other',
  'appliance-repair': 'other',
  irrigation: 'other',
  'chimney-fireplace': 'other',
  septic: 'other',
  'water-treatment': 'other',
} as const;

export type NicheSlug = keyof typeof NICHE_TO_TRADE_TYPE;

export const NICHE_SLUGS = Object.keys(NICHE_TO_TRADE_TYPE) as NicheSlug[];

export interface NicheFaq {
  q: string;
  a: string;
}

export interface NicheProblem {
  title: string;
  desc: string;
}

export interface NicheResourceLink {
  slug: string;
  label: string;
}

export interface NicheMockupRow {
  name: string;
  reason: string;
  risk: 'High risk' | 'Medium risk' | 'Low risk';
}

export interface NicheConfig {
  slug: NicheSlug;
  trade: string;
  audience: string;
  heroTitle: string;
  heroSubhead: string;
  badge: string;
  companyName: string;
  planLabel: string;
  planExamples: string[];
  equipmentExamples: string[];
  seasonalHook: string;
  problems: NicheProblem[];
  revenueExample: {
    agreementCount: number;
    avgPlanPrice: number;
    lapseRatePercent: number;
    pullThroughNote: string;
  };
  fsmTools: string[];
  features: { title: string; desc: string }[];
  faqs: NicheFaq[];
  resources: NicheResourceLink[];
  seo: { title: string; description: string };
}

const DEFAULT_PROBLEMS: NicheProblem[] = [
  {
    title: 'Quiet drop-offs',
    desc: 'Members skip visits, ignore renewal emails, or let cards expire — then lapse without calling to cancel.',
  },
  {
    title: 'Manual chasing',
    desc: 'Someone calls down a spreadsheet alphabetically while high-value accounts wait. Half the list never gets dialed.',
  },
  {
    title: 'Zero visibility',
    desc: "You can't answer how much renewal revenue is at risk this quarter until it's already lost.",
  },
];

const DEFAULT_FEATURES = (trade: string, plan: string): NicheConfig['features'] => [
  {
    title: 'Renewal risk dashboard',
    desc: `Every ${plan} scored Low / Medium / High with plain-language reasons your office can act on.`,
  },
  {
    title: 'Renewal forecast',
    desc: `See how many ${trade.toLowerCase()} members are likely to lapse in the next 90 days — before busy season.`,
  },
  {
    title: 'Call-this-week worklist',
    desc: 'Ranked by risk and dollars at risk — phone number and one-line reason on one screen.',
  },
  {
    title: 'Automated renewal campaigns',
    desc: 'Email and SMS from 60 days out through win-back — without someone dialing hundreds of names by hand.',
  },
  {
    title: 'One-click renewal payments',
    desc: 'Members tap a link and pay. The plan renews and reminders stop.',
  },
  {
    title: 'Revenue-at-risk forecast',
    desc: 'Active agreements, dollars at risk, and share likely to lapse — updated daily.',
  },
  {
    title: 'Equipment & upgrade leads',
    desc: `Spot aging equipment and turn renewals into replacement conversations.`,
  },
  {
    title: 'Works with your tools',
    desc: 'Connect your FSM, QuickBooks, or import a spreadsheet — we never ask you to switch.',
  },
];

export const NICHE_CONFIGS: NicheConfig[] = [
  {
    slug: 'hvac',
    trade: 'HVAC',
    audience: 'HVAC contractors',
    heroTitle: 'Stop losing HVAC maintenance customers',
    heroSubhead:
      'See which Comfort Plan members are about to lapse, who to call this week, and automate email, text, and payment follow-ups — so renewals don\'t die in your inbox during peak season.',
    badge: 'For HVAC contractors · Works with ServiceTitan, Jobber, Housecall Pro',
    companyName: "Mike's Comfort HVAC",
    planLabel: 'Comfort Plans',
    planExamples: ['Comfort Plan', 'Priority Maintenance', 'Gold Shield'],
    equipmentExamples: ['furnace', 'air conditioner', 'heat pump', 'mini-split'],
    seasonalHook: 'before peak cooling and heating season',
    problems: [
      {
        title: 'Quiet drop-offs',
        desc: 'Customers skip the spring tune-up, ignore renewal emails during peak season, or let a card expire — then quietly lapse.',
      },
      {
        title: 'Manual chasing',
        desc: 'Someone calls down a spreadsheet while commercial accounts wait. Half the list never gets dialed.',
      },
      {
        title: 'Zero visibility',
        desc: "Owners can't answer how much renewal revenue is at risk until it's lost. FSM software optimizes jobs, not renewals.",
      },
    ],
    revenueExample: {
      agreementCount: 320,
      avgPlanPrice: 295,
      lapseRatePercent: 18,
      pullThroughNote: '$40k–$55k total customer value at risk including repairs and replacements',
    },
    fsmTools: ['ServiceTitan', 'Jobber', 'Housecall Pro', 'QuickBooks'],
    features: DEFAULT_FEATURES('HVAC', 'Comfort Plan'),
    faqs: [
      {
        q: 'What is the best software for HVAC maintenance agreement renewals?',
        a: 'Look for renewal risk scoring, a prioritized call list, multi-touch campaigns, and one-click payment — not just expiry reminders. Renewal Drive is built only for that job.',
      },
      {
        q: 'Does Renewal Drive replace ServiceTitan?',
        a: 'No. Keep ServiceTitan for operations. Use Renewal Drive to see who will not renew and automate follow-up.',
      },
      {
        q: 'Can I import my member list from a spreadsheet?',
        a: 'Yes. Upload CSV with name, email, phone, plan name, renewal date, and last service date. Most shops are live in under 10 minutes.',
      },
      {
        q: 'What renewal rate should HVAC shops target?',
        a: '78%+ is median for residential HVAC; top shops hit 88%+. See our maintenance agreement renewal rate benchmark.',
      },
      {
        q: 'Will this work for commercial HVAC?',
        a: 'Yes for agreement-based PM contracts. Per-job T&M commercial is a different workflow.',
      },
      {
        q: 'Is SMS compliant?',
        a: 'Growth plan and above include SMS steps with opt-in tracking for US TCPA and GDPR-conscious workflows.',
      },
    ],
    resources: [
      { slug: 'reduce-hvac-customer-churn', label: 'How to reduce HVAC customer churn' },
      { slug: 'win-back-lapsed-hvac-customers', label: 'Win back lapsed HVAC customers' },
      { slug: 'create-hvac-maintenance-agreement-template', label: 'HVAC maintenance agreement template' },
    ],
    seo: {
      title: 'HVAC Maintenance Agreement Software | Renewal Drive',
      description:
        'Stop losing HVAC maintenance customers. See who won\'t renew, who to call first, and automate tune-up reminders and one-click renewals. Works with ServiceTitan & Jobber.',
    },
  },
  {
    slug: 'plumbing',
    trade: 'Plumbing',
    audience: 'plumbing contractors',
    heroTitle: 'Stop losing plumbing maintenance customers',
    heroSubhead:
      'See which Pipe Shield members are about to lapse, who to call this week, and automate follow-ups — so renewals don\'t sit in voicemail while you\'re on emergency calls.',
    badge: 'For plumbing contractors · Works with ServiceTitan, Jobber, Housecall Pro',
    companyName: 'ClearFlow Plumbing',
    planLabel: 'Pipe Shield members',
    planExamples: ['Pipe Shield Plan', 'Priority Plumbing Club', 'Gold Drain Care'],
    equipmentExamples: ['water heater', 'sump pump', 'backflow preventer', 'tankless unit'],
    seasonalHook: 'before winter freeze-ups and spring inspection season',
    problems: DEFAULT_PROBLEMS,
    revenueExample: {
      agreementCount: 180,
      avgPlanPrice: 240,
      lapseRatePercent: 20,
      pullThroughNote: '$25k–$35k total value at risk including emergency call pull-through',
    },
    fsmTools: ['ServiceTitan', 'Jobber', 'Housecall Pro', 'QuickBooks'],
    features: DEFAULT_FEATURES('plumbing', 'Pipe Shield Plan'),
    faqs: [
      {
        q: 'Best software for plumbing service agreement renewals?',
        a: 'Renewal Drive scores lapse risk, ranks your call list, and automates email, SMS, and payment links — alongside the FSM you already use.',
      },
      {
        q: 'We only have 40 maintenance members. Is it worth it?',
        a: 'Yes. Lite tier starts at $19/mo. Recovering one $240 plan often pays for the year when you count pull-through repairs.',
      },
      {
        q: 'Can plumbers import from Jobber?',
        a: 'Yes — CSV import today; direct integrations on Pro tier.',
      },
      {
        q: 'Does this help with water heater club renewals?',
        a: 'Yes. Track plan name, renewal date, and last service to flag members who skipped their annual inspection.',
      },
      {
        q: 'What renewal rate should plumbers target?',
        a: '76%+ median for residential plumbing plans; top quartile 86%+.',
      },
      {
        q: 'Replace ServiceTitan?',
        a: 'No — we sit beside it and fix the renewal gap.',
      },
    ],
    resources: [
      { slug: 'plumbing-service-agreement-template', label: 'Plumbing service agreement template' },
      { slug: 'price-maintenance-plan-service-agreement', label: 'How to price a maintenance plan' },
      { slug: 'win-back-email-text-templates-service-business', label: 'Win-back email and text templates' },
    ],
    seo: {
      title: 'Plumbing Maintenance Agreement Software | Renewal Drive',
      description:
        'Stop losing plumbing maintenance members. See who won\'t renew, automate drain-care reminders and one-click renewals. Works with ServiceTitan & Jobber.',
    },
  },
  {
    slug: 'electrical',
    trade: 'Electrical',
    audience: 'electrical contractors',
    heroTitle: 'Stop losing electrical maintenance customers',
    heroSubhead:
      'See which Power Guard members are about to lapse, who to call first, and automate safety-inspection renewals — before storm season fills your board.',
    badge: 'For electrical contractors · Works with ServiceTitan, Jobber, Housecall Pro',
    companyName: 'BrightWire Electric',
    planLabel: 'Power Guard plans',
    planExamples: ['Power Guard Plan', 'Safety First Club', 'Priority Electrical Care'],
    equipmentExamples: ['panel', 'generator', 'EV charger', 'smoke/CO detectors'],
    seasonalHook: 'before storm season and generator demand spikes',
    problems: DEFAULT_PROBLEMS,
    revenueExample: {
      agreementCount: 140,
      avgPlanPrice: 220,
      lapseRatePercent: 22,
      pullThroughNote: '$20k–$30k at risk including panel upgrades and generator work',
    },
    fsmTools: ['ServiceTitan', 'Jobber', 'Housecall Pro', 'QuickBooks'],
    features: DEFAULT_FEATURES('electrical', 'Power Guard Plan'),
    faqs: [
      {
        q: 'Software for electrical service plan renewals?',
        a: 'Renewal Drive flags members who skipped inspections, ranks who to call, and automates renewal campaigns.',
      },
      {
        q: 'Works with generator maintenance plans?',
        a: 'Yes — track renewal dates and last service for generator and panel inspection programmes.',
      },
      {
        q: 'Import from spreadsheet?',
        a: 'Yes. Live in minutes with CSV.',
      },
      {
        q: 'Target renewal rate?',
        a: '74%+ median for electrical; top shops 84%+.',
      },
      {
        q: 'Replace my FSM?',
        a: 'No — complement only.',
      },
      {
        q: 'EV charger maintenance plans?',
        a: 'Yes — any recurring agreement with a renewal date.',
      },
    ],
    resources: [
      { slug: 'early-warning-signs-maintenance-customer-leaving', label: '7 early warning signs a customer is leaving' },
      { slug: 'find-upgrade-opportunities-customer-base', label: 'Find upgrade opportunities in your base' },
      { slug: 'automate-maintenance-renewal-reminders', label: 'Automate maintenance renewal reminders' },
    ],
    seo: {
      title: 'Electrical Maintenance Agreement Software | Renewal Drive',
      description:
        'Stop losing electrical service plan members. Risk scoring, call lists, and automated renewals for electrical contractors. Works with Jobber & ServiceTitan.',
    },
  },
  {
    slug: 'pest-control',
    trade: 'Pest Control',
    audience: 'pest control companies',
    heroTitle: 'Stop losing pest control service plan members',
    heroSubhead:
      'See which Shield Plan accounts are about to lapse, who to call this week, and automate quarterly renewal follow-ups — before spring swarm season.',
    badge: 'For pest control companies · Works with PestPac, FieldRoutes, Jobber',
    companyName: 'Guardian Pest Solutions',
    planLabel: 'Shield Plans',
    planExamples: ['Shield Plan', 'Quarterly Protection', 'Premium Pest Guard'],
    equipmentExamples: ['termite bond', 'mosquito programme', 'rodent monitoring', 'perimeter treatment'],
    seasonalHook: 'before spring swarm and fall rodent migration',
    problems: DEFAULT_PROBLEMS,
    revenueExample: {
      agreementCount: 450,
      avgPlanPrice: 180,
      lapseRatePercent: 16,
      pullThroughNote: '$35k+ agreement revenue at risk; quarterly cadence makes lapses costly',
    },
    fsmTools: ['PestPac', 'FieldRoutes', 'Jobber', 'Spreadsheets'],
    features: DEFAULT_FEATURES('pest control', 'Shield Plan'),
    faqs: [
      {
        q: 'Pest control renewal software?',
        a: 'Renewal Drive tracks quarterly and annual plans, scores lapse risk, and automates win-back for skipped treatments.',
      },
      {
        q: 'Works with PestPac?',
        a: 'CSV import today; integrations expanding on Pro tier.',
      },
      {
        q: 'Quarterly plan renewals?',
        a: 'Yes — track any interval with renewal dates and last service.',
      },
      {
        q: 'Target renewal rate?',
        a: '82%+ median for pest control; top quartile 91%+.',
      },
      {
        q: 'SMS reminders?',
        a: 'Growth plan includes SMS campaign steps with opt-in.',
      },
      {
        q: 'Replace PestPac?',
        a: 'No — we fix renewals on top.',
      },
    ],
    resources: [
      { slug: 'when-to-send-win-back-offer', label: 'When to send a win-back offer' },
      { slug: 'annual-vs-monthly-maintenance-memberships', label: 'Annual vs monthly memberships' },
      { slug: 'build-call-this-week-list-renewals', label: 'Build a call-this-week renewal list' },
    ],
    seo: {
      title: 'Pest Control Service Plan Renewal Software | Renewal Drive',
      description:
        'Stop losing pest control service plan members. Automate quarterly renewal reminders, risk scoring, and win-back campaigns.',
    },
  },
  {
    slug: 'lawn-care',
    trade: 'Lawn Care',
    audience: 'lawn care and landscaping companies',
    heroTitle: 'Stop losing lawn care program members',
    heroSubhead:
      'See which Green Shield accounts won\'t renew, who to call before spring signup season, and automate membership follow-ups — without another spreadsheet.',
    badge: 'For lawn care & landscaping · Works with LMN, Jobber, Aspire',
    companyName: 'GreenEdge Lawn Co.',
    planLabel: 'Green Shield programs',
    planExamples: ['Green Shield Program', 'Seasonal Care Club', 'Premium Turf Plan'],
    equipmentExamples: ['fertilization programme', 'aeration', 'irrigation check', 'mosquito treatment'],
    seasonalHook: 'before spring signup and fall aeration renewals',
    problems: DEFAULT_PROBLEMS,
    revenueExample: {
      agreementCount: 280,
      avgPlanPrice: 165,
      lapseRatePercent: 24,
      pullThroughNote: '$28k+ at risk — seasonal pauses without formal cancel drive lapses',
    },
    fsmTools: ['LMN', 'Jobber', 'Aspire', 'Spreadsheets'],
    features: DEFAULT_FEATURES('lawn care', 'Green Shield Program'),
    faqs: [
      {
        q: 'Lawn care membership renewal software?',
        a: 'Renewal Drive flags seasonal pauses, ranks who to call, and automates spring renewal campaigns.',
      },
      {
        q: 'Seasonal vs year-round programmes?',
        a: 'Both — track renewal dates and last visit for any cadence.',
      },
      {
        q: 'Import from LMN?',
        a: 'CSV export/import works today.',
      },
      {
        q: 'Target renewal rate?',
        a: '71%+ median for lawn care; top shops 83%+.',
      },
      {
        q: 'Monthly vs annual billing?',
        a: 'See our annual vs monthly membership guide — both supported.',
      },
      {
        q: 'Replace Jobber?',
        a: 'No — complement for retention only.',
      },
    ],
    resources: [
      { slug: 'annual-vs-monthly-maintenance-memberships', label: 'Annual vs monthly memberships' },
      { slug: 'price-maintenance-plan-service-agreement', label: 'How to price a maintenance plan' },
      { slug: 'track-maintenance-agreements-spreadsheet-vs-software', label: 'Spreadsheet vs software' },
    ],
    seo: {
      title: 'Lawn Care Membership Renewal Software | Renewal Drive',
      description:
        'Stop losing lawn care program members. Risk dashboard, call lists, and automated renewals for landscapers. Works with LMN & Jobber.',
    },
  },
  {
    slug: 'pool-spa',
    trade: 'Pool & Spa',
    audience: 'pool and spa service companies',
    heroTitle: 'Stop losing pool service plan members',
    heroSubhead:
      'See which Crystal Clear accounts are about to lapse, who to call before opening season, and automate opening/closing renewal follow-ups.',
    badge: 'For pool & spa service · Works with Skimmer, Pool Brain, Jobber',
    companyName: 'AquaPro Pool Service',
    planLabel: 'Crystal Clear plans',
    planExamples: ['Crystal Clear Plan', 'Weekly Service Club', 'Premium Pool Care'],
    equipmentExamples: ['pool opening', 'winterization', 'filter service', 'salt system'],
    seasonalHook: 'before opening season and winterization renewals',
    problems: DEFAULT_PROBLEMS,
    revenueExample: {
      agreementCount: 220,
      avgPlanPrice: 210,
      lapseRatePercent: 19,
      pullThroughNote: '$30k+ at risk — skipped openings often precede lapse',
    },
    fsmTools: ['Skimmer', 'Pool Brain', 'Jobber', 'Spreadsheets'],
    features: DEFAULT_FEATURES('pool service', 'Crystal Clear Plan'),
    faqs: [
      {
        q: 'Pool service agreement renewal software?',
        a: 'Renewal Drive scores members who skipped opening or closing, automates seasonal renewal campaigns.',
      },
      {
        q: 'Works with Skimmer?',
        a: 'CSV import from Skimmer exports today.',
      },
      {
        q: 'Seasonal plans?',
        a: 'Yes — opening/closing dates and renewal tracking.',
      },
      {
        q: 'Target renewal rate?',
        a: '79%+ median for pool service; top quartile 89%+.',
      },
      {
        q: 'Weekly service contracts?',
        a: 'Any recurring agreement with renewal date.',
      },
      {
        q: 'Replace Pool Brain?',
        a: 'No — retention layer only.',
      },
    ],
    resources: [
      { slug: 'annual-vs-monthly-maintenance-memberships', label: 'Annual vs monthly memberships' },
      { slug: 'automate-maintenance-renewal-reminders', label: 'Automate renewal reminders' },
      { slug: 'calculate-revenue-at-risk-service-business', label: 'Calculate revenue at risk' },
    ],
    seo: {
      title: 'Pool Service Agreement Renewal Software | Renewal Drive',
      description:
        'Stop losing pool service plan members. Opening-season renewals, risk scoring, and automated follow-ups for pool pros.',
    },
  },
  {
    slug: 'garage-door',
    trade: 'Garage Door',
    audience: 'garage door companies',
    heroTitle: 'Stop losing garage door maintenance members',
    heroSubhead:
      'See which Door Guard members won\'t renew, who to call first, and automate spring tune-up reminders — before cold cycles drive emergency calls only.',
    badge: 'For garage door companies · Works with Jobber, Housecall Pro',
    companyName: 'Overhead Pros',
    planLabel: 'Door Guard plans',
    planExamples: ['Door Guard Plan', 'Spring Tune-Up Club', 'Premium Door Care'],
    equipmentExamples: ['opener', 'springs', 'cable', 'safety sensors'],
    seasonalHook: 'before winter cold cycles and spring opener upgrades',
    problems: DEFAULT_PROBLEMS,
    revenueExample: { agreementCount: 95, avgPlanPrice: 175, lapseRatePercent: 21, pullThroughNote: '$12k+ at risk plus opener replacement pull-through' },
    fsmTools: ['Jobber', 'Housecall Pro', 'Spreadsheets'],
    features: DEFAULT_FEATURES('garage door', 'Door Guard Plan'),
    faqs: [
      { q: 'Garage door maintenance plan software?', a: 'Renewal Drive automates tune-up renewal campaigns and ranks at-risk members.' },
      { q: 'Small shop with 50 plans?', a: 'Lite tier from $19/mo fits small books.' },
      { q: 'CSV import?', a: 'Yes — live in minutes.' },
      { q: 'Replace Jobber?', a: 'No.' },
      { q: 'Annual tune-up plans?', a: 'Yes — any renewal-dated agreement.' },
      { q: 'SMS?', a: 'Available on Growth plan and above.' },
    ],
    resources: [
      { slug: 'maintenance-agreement-renewal-rate-benchmark', label: 'Renewal rate benchmark' },
      { slug: 'calculate-revenue-at-risk-service-business', label: 'Calculate revenue at risk' },
      { slug: 'import-customer-list-spreadsheet-5-minutes', label: 'Import your list in 5 minutes' },
    ],
    seo: {
      title: 'Garage Door Maintenance Plan Software | Renewal Drive',
      description: 'Automate garage door maintenance plan renewals. Risk scoring, call lists, and one-click payments.',
    },
  },
  {
    slug: 'appliance-repair',
    trade: 'Appliance Repair',
    audience: 'appliance repair companies',
    heroTitle: 'Stop losing appliance maintenance members',
    heroSubhead:
      'See which Appliance Shield members are slipping away and automate renewal follow-ups before holiday cooking season.',
    badge: 'For appliance repair · Works with Jobber, Housecall Pro',
    companyName: 'FixIt Appliance Care',
    planLabel: 'Appliance Shield plans',
    planExamples: ['Appliance Shield Plan', 'Kitchen Care Club', 'Home Appliance Guard'],
    equipmentExamples: ['refrigerator', 'washer/dryer', 'dishwasher', 'range'],
    seasonalHook: 'before holiday cooking and spring upgrade cycles',
    problems: DEFAULT_PROBLEMS,
    revenueExample: { agreementCount: 110, avgPlanPrice: 160, lapseRatePercent: 23, pullThroughNote: '$14k+ agreement revenue at risk' },
    fsmTools: ['Jobber', 'Housecall Pro', 'Spreadsheets'],
    features: DEFAULT_FEATURES('appliance repair', 'Appliance Shield Plan'),
    faqs: [
      { q: 'Appliance maintenance plan renewals?', a: 'Track renewal dates, flag skipped visits, automate campaigns.' },
      { q: 'Small membership base?', a: 'Start on Lite — scale as you grow.' },
      { q: 'Import spreadsheet?', a: 'Yes.' },
      { q: 'Multi-appliance plans?', a: 'Track any plan name and renewal date per customer.' },
      { q: 'FSM replacement?', a: 'No — retention only.' },
      { q: 'Win-back for lapsed members?', a: 'Growth plan includes win-back campaigns.' },
    ],
    resources: [
      { slug: 'maintenance-agreement-renewal-rate-benchmark', label: 'Renewal rate benchmark' },
      { slug: 'calculate-revenue-at-risk-service-business', label: 'Calculate revenue at risk' },
      { slug: 'import-customer-list-spreadsheet-5-minutes', label: 'Import in 5 minutes' },
    ],
    seo: {
      title: 'Appliance Maintenance Plan Software | Renewal Drive',
      description: 'Automate appliance maintenance plan renewals with risk scoring and one-click payments.',
    },
  },
  {
    slug: 'irrigation',
    trade: 'Irrigation',
    audience: 'irrigation and sprinkler companies',
    heroTitle: 'Stop losing irrigation maintenance members',
    heroSubhead:
      'See which Sprinkler Shield accounts won\'t renew and automate spring startup and fall winterization renewal campaigns.',
    badge: 'For irrigation contractors · Works with Jobber, LMN',
    companyName: 'SprinklerTech Irrigation',
    planLabel: 'Sprinkler Shield plans',
    planExamples: ['Sprinkler Shield Plan', 'Seasonal Startup Club', 'Year-Round Irrigation Care'],
    equipmentExamples: ['controller', 'backflow', 'zone valves', 'winterization'],
    seasonalHook: 'before spring startup and fall winterization',
    problems: DEFAULT_PROBLEMS,
    revenueExample: { agreementCount: 130, avgPlanPrice: 190, lapseRatePercent: 20, pullThroughNote: '$18k+ at risk — skipped startup predicts lapse' },
    fsmTools: ['Jobber', 'LMN', 'Spreadsheets'],
    features: DEFAULT_FEATURES('irrigation', 'Sprinkler Shield Plan'),
    faqs: [
      { q: 'Irrigation plan renewal software?', a: 'Seasonal renewal tracking and automated startup/winterization reminders.' },
      { q: 'Spring-only customers?', a: 'Flag members who skip startup as high risk.' },
      { q: 'CSV import?', a: 'Yes.' },
      { q: 'LMN compatible?', a: 'Export CSV from LMN and import.' },
      { q: 'Target renewal rate?', a: 'Aim for 75%+ with automated seasonal campaigns.' },
      { q: 'Replace Jobber?', a: 'No.' },
    ],
    resources: [
      { slug: 'maintenance-agreement-renewal-rate-benchmark', label: 'Renewal rate benchmark' },
      { slug: 'calculate-revenue-at-risk-service-business', label: 'Revenue at risk' },
      { slug: 'automate-maintenance-renewal-reminders', label: 'Automate reminders' },
    ],
    seo: {
      title: 'Irrigation Maintenance Plan Software | Renewal Drive',
      description: 'Automate sprinkler and irrigation plan renewals. Seasonal campaigns and risk scoring.',
    },
  },
  {
    slug: 'chimney-fireplace',
    trade: 'Chimney & Fireplace',
    audience: 'chimney and fireplace service companies',
    heroTitle: 'Stop losing chimney maintenance members',
    heroSubhead:
      'See which Hearth Guard members won\'t renew and automate fall inspection renewal campaigns before burning season.',
    badge: 'For chimney & fireplace pros · Works with Jobber, Housecall Pro',
    companyName: 'HearthSafe Chimney',
    planLabel: 'Hearth Guard plans',
    planExamples: ['Hearth Guard Plan', 'Annual Sweep Club', 'Fireplace Care Plan'],
    equipmentExamples: ['chimney sweep', 'fireplace inspection', 'cap install', 'dryer vent'],
    seasonalHook: 'before fall burning season and spring inspections',
    problems: DEFAULT_PROBLEMS,
    revenueExample: { agreementCount: 85, avgPlanPrice: 200, lapseRatePercent: 22, pullThroughNote: '$11k+ at risk — annual sweep drives the relationship' },
    fsmTools: ['Jobber', 'Housecall Pro', 'Spreadsheets'],
    features: DEFAULT_FEATURES('chimney', 'Hearth Guard Plan'),
    faqs: [
      { q: 'Chimney sweep plan renewals?', a: 'Automate annual inspection renewal reminders and win-back.' },
      { q: 'Highly seasonal?', a: 'Start campaigns 60 days before sweep due date.' },
      { q: 'Small book?', a: 'Lite tier works from 25 agreements.' },
      { q: 'Import list?', a: 'CSV in minutes.' },
      { q: 'Dryer vent add-ons?', a: 'Track any plan per customer.' },
      { q: 'FSM replacement?', a: 'No.' },
    ],
    resources: [
      { slug: 'maintenance-agreement-renewal-rate-benchmark', label: 'Renewal benchmark' },
      { slug: 'win-back-lapsed-hvac-customers', label: 'Win-back playbook (adapt for chimney)' },
      { slug: 'maintenance-agreement-email-templates', label: 'Renewal email templates' },
    ],
    seo: {
      title: 'Chimney Maintenance Plan Software | Renewal Drive',
      description: 'Automate chimney sweep and fireplace plan renewals. Fall-season campaigns and risk scoring.',
    },
  },
  {
    slug: 'septic',
    trade: 'Septic',
    audience: 'septic service companies',
    heroTitle: 'Stop losing septic maintenance members',
    heroSubhead:
      'See which Septic Shield accounts are about to lapse and automate pumping and inspection renewal reminders.',
    badge: 'For septic service companies · Works with Jobber, spreadsheets',
    companyName: 'ClearTank Septic',
    planLabel: 'Septic Shield plans',
    planExamples: ['Septic Shield Plan', 'Annual Pump Club', 'Inspection Care Plan'],
    equipmentExamples: ['tank pumping', 'inspection', 'baffle repair', 'alarm check'],
    seasonalHook: 'before spring inspections and pre-winter pumping',
    problems: DEFAULT_PROBLEMS,
    revenueExample: { agreementCount: 70, avgPlanPrice: 250, lapseRatePercent: 19, pullThroughNote: '$10k+ at risk — missed pump cycles predict lapse' },
    fsmTools: ['Jobber', 'Spreadsheets'],
    features: DEFAULT_FEATURES('septic', 'Septic Shield Plan'),
    faqs: [
      { q: 'Septic maintenance plan software?', a: 'Track pumping schedules, renewal dates, and automate reminders.' },
      { q: '3-year pump intervals?', a: 'Set interval per agreement — flag overdue service.' },
      { q: 'Rural routes?', a: 'Call-this-week list ranks by risk and revenue.' },
      { q: 'CSV import?', a: 'Yes.' },
      { q: 'Small operator?', a: 'Lite from $19/mo.' },
      { q: 'Replace Jobber?', a: 'No.' },
    ],
    resources: [
      { slug: 'maintenance-agreement-renewal-rate-benchmark', label: 'Renewal benchmark' },
      { slug: 'calculate-revenue-at-risk-service-business', label: 'Revenue at risk' },
      { slug: 'import-customer-list-spreadsheet-5-minutes', label: 'Import in 5 minutes' },
    ],
    seo: {
      title: 'Septic Maintenance Plan Software | Renewal Drive',
      description: 'Automate septic service plan renewals. Pumping reminders, risk scoring, win-back campaigns.',
    },
  },
  {
    slug: 'water-treatment',
    trade: 'Water Treatment',
    audience: 'water treatment service companies',
    heroTitle: 'Stop losing water treatment plan members',
    heroSubhead:
      'See which Pure Water members won\'t renew and automate filter-change and system inspection renewal follow-ups.',
    badge: 'For water treatment pros · Works with Jobber, ServiceTitan',
    companyName: 'PureFlow Water Treatment',
    planLabel: 'Pure Water plans',
    planExamples: ['Pure Water Plan', 'Filter Service Club', 'Softener Care Plan'],
    equipmentExamples: ['softener', 'RO system', 'filter cartridge', 'UV lamp'],
    seasonalHook: 'before holiday guest season and spring water quality checks',
    problems: DEFAULT_PROBLEMS,
    revenueExample: { agreementCount: 90, avgPlanPrice: 185, lapseRatePercent: 21, pullThroughNote: '$13k+ at risk — skipped filter changes predict cancel' },
    fsmTools: ['Jobber', 'ServiceTitan', 'Spreadsheets'],
    features: DEFAULT_FEATURES('water treatment', 'Pure Water Plan'),
    faqs: [
      { q: 'Water treatment plan renewals?', a: 'Track filter service dates, score lapse risk, automate renewals.' },
      { q: 'Salt delivery + service plans?', a: 'Track service agreements separately from delivery.' },
      { q: 'Import CSV?', a: 'Yes — live in minutes.' },
      { q: 'Monthly filter plans?', a: 'Any renewal interval supported.' },
      { q: 'SMS reminders?', a: 'Growth plan and above.' },
      { q: 'FSM replacement?', a: 'No — retention layer.' },
    ],
    resources: [
      { slug: 'maintenance-agreement-renewal-rate-benchmark', label: 'Renewal benchmark' },
      { slug: 'calculate-revenue-at-risk-service-business', label: 'Revenue at risk' },
      { slug: 'import-customer-list-spreadsheet-5-minutes', label: 'Import in 5 minutes' },
    ],
    seo: {
      title: 'Water Treatment Plan Renewal Software | Renewal Drive',
      description: 'Automate water treatment service plan renewals. Filter service reminders and risk dashboard.',
    },
  },
];

export function getNicheConfig(slug: string): NicheConfig | undefined {
  return NICHE_CONFIGS.find((n) => n.slug === slug);
}

export function getDefaultPlanNameForNiche(slug: NicheSlug): string {
  const n = getNicheConfig(slug);
  if (!n) return 'Annual Maintenance Plan';
  return `Annual ${n.planExamples[0] ?? n.trade + ' Maintenance'}`;
}

export const NICHE_MOCKUP_ROWS: Record<NicheSlug, NicheMockupRow[]> = {
  hvac: [
    { name: 'Johnson Residence', reason: 'Renewal 8 days overdue · unpaid invoice', risk: 'High risk' },
    { name: 'Tampa Bay Offices', reason: 'No cooling tune-up in 13 months', risk: 'High risk' },
    { name: 'Riverside Cafe', reason: 'Renews in 20 days · not opening emails', risk: 'Medium risk' },
  ],
  plumbing: [
    { name: 'Walsh Home', reason: 'Water heater flush overdue · renewal in 14 days', risk: 'High risk' },
    { name: 'Oak Street Duplex', reason: 'Unpaid invoice on last visit', risk: 'High risk' },
    { name: 'Chen Residence', reason: 'Not opening renewal emails', risk: 'Medium risk' },
  ],
  electrical: [
    { name: 'Miller Home', reason: 'Panel inspection overdue 11 months', risk: 'High risk' },
    { name: 'Lakeview Retail', reason: 'Generator test skipped', risk: 'Medium risk' },
    { name: 'Patel Residence', reason: 'Renews in 12 days', risk: 'Medium risk' },
  ],
  'pest-control': [
    { name: 'Brooks Residence', reason: 'Missed Q2 treatment · renewal due', risk: 'High risk' },
    { name: 'Sunset Apartments', reason: 'Termite bond expires in 21 days', risk: 'High risk' },
    { name: 'Garcia Home', reason: 'Price increase · no response', risk: 'Medium risk' },
  ],
  'lawn-care': [
    { name: 'Thompson Yard', reason: 'Skipped spring fertilization', risk: 'High risk' },
    { name: 'Parkside HOA', reason: 'Renewal 30 days out · no aeration booked', risk: 'Medium risk' },
    { name: 'Lee Residence', reason: 'Paused service · no formal hold', risk: 'High risk' },
  ],
  'pool-spa': [
    { name: 'Beach House', reason: 'Opening skipped · renewal in 18 days', risk: 'High risk' },
    { name: 'Collins Pool', reason: 'Filter service 4 months overdue', risk: 'High risk' },
    { name: 'Marina Club', reason: 'Not opening emails', risk: 'Medium risk' },
  ],
  'garage-door': [
    { name: 'Foster Garage', reason: 'Spring tune-up overdue', risk: 'High risk' },
    { name: 'Auto Shop Bay 3', reason: 'Renewal overdue 5 days', risk: 'High risk' },
    { name: 'Nguyen Home', reason: 'Opener quote pending · plan expiring', risk: 'Medium risk' },
  ],
  'appliance-repair': [
    { name: 'Smith Kitchen', reason: 'No service in 14 months', risk: 'High risk' },
    { name: 'Davis Condo', reason: 'Renewal in 10 days · dishwasher claim open', risk: 'Medium risk' },
    { name: 'Brown Residence', reason: 'Card expired', risk: 'High risk' },
  ],
  irrigation: [
    { name: 'Greenfield HOA', reason: 'Startup not scheduled · renewal due', risk: 'High risk' },
    { name: 'Adams Yard', reason: 'Backflow test overdue', risk: 'High risk' },
    { name: 'Villa Park', reason: 'Winterization skipped last fall', risk: 'Medium risk' },
  ],
  'chimney-fireplace': [
    { name: 'Stone Cottage', reason: 'Sweep overdue before burn season', risk: 'High risk' },
    { name: 'Mountain Lodge', reason: 'Renewal 22 days out', risk: 'Medium risk' },
    { name: 'Reed Home', reason: 'Inspection no-show', risk: 'High risk' },
  ],
  septic: [
    { name: 'Farmhouse Lane', reason: 'Pump cycle overdue 8 months', risk: 'High risk' },
    { name: 'Creek Road', reason: 'Renewal overdue', risk: 'High risk' },
    { name: 'Hilltop Cabin', reason: 'Inspection skipped', risk: 'Medium risk' },
  ],
  'water-treatment': [
    { name: 'Filter Lane Home', reason: 'Cartridge change 3 months late', risk: 'High risk' },
    { name: 'Well House #2', reason: 'Renewal in 15 days', risk: 'Medium risk' },
    { name: 'Springs Estate', reason: 'UV lamp not replaced', risk: 'High risk' },
  ],
};
